/**
 * §A.4 · El CRM de leads.
 *
 * ## Las dos cifras que impiden que el embudo se convierta en un archivo
 *
 * `overdue_actions` —«una próxima acción con fecha pasada es una promesa que nos
 * hicimos y no cumplimos»— y `days_since_update`, que hace visible «el lead que lleva
 * cinco semanas en demo porque nadie volvió a llamar». Las dos van delante en la
 * pantalla, porque son las únicas que piden hacer algo hoy.
 *
 * `days_since_update` viaja **`null` en un lead cerrado**: ahí no significa nada, y
 * pintar «cerrado hace 40 días» al lado de los parados sería ruido.
 *
 * ## `conversion_rate` es sobre los cerrados del mes
 *
 * No sobre el embudo entero: «dividir entre todos los leads daría una conversión que
 * baja cada vez que entra uno nuevo, que es exactamente al revés de lo que
 * significa». La pantalla lo dice, porque la cifra se va a copiar a algún sitio.
 */

export type EtapaDeLead =
  'contactado' | 'demo' | 'prueba' | 'ganado' | 'perdido';

export type CanalDeLead =
  | 'contacto'
  | 'campeonato'
  | 'seo'
  | 'recomendacion'
  | 'directorio'
  | 'landing'
  | 'otro';

export interface Lead {
  readonly id: string;
  /** El club, cuando ya se convirtió. **Es lo que cierra el embudo.** */
  readonly academy_id: string | null;
  readonly academy_name: string | null;
  readonly club_name: string;
  readonly contact_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly city: string | null;
  readonly province: string | null;
  /** Qué enseña, por código (`taekwondo`…). Al convertirlo, las del club. */
  readonly discipline_codes: readonly string[];
  /** Los alumnos que dijo tener en la web: `1-30`, `30-80`, `80-200`, `200+`. */
  readonly students_range: string | null;
  readonly source: CanalDeLead | null;
  readonly stage: EtapaDeLead;
  readonly owner_user_id: string | null;
  readonly owner_name: string | null;
  readonly lost_reason: string | null;
  readonly next_action_on: Date | string | null;
  readonly notes: string | null;
  /** Días parado, **solo mientras sigue abierto**. Ver la cabecera. */
  readonly days_since_update: number | null;
  readonly created_at: Date | string;
  readonly updated_at: Date | string;
}

export interface Embudo {
  readonly stages: readonly {
    readonly stage: EtapaDeLead;
    readonly leads: number;
    /** La cifra que se mira. Ver la cabecera. */
    readonly overdue_actions: number;
    /** Sin tocar en tres semanas. */
    readonly stale: number;
    readonly next_action: Date | string | null;
  }[];
  readonly total_open: number;
  readonly won_this_month: number;
  readonly lost_this_month: number;
  /** Sobre los **cerrados** del mes. Ver la cabecera. */
  readonly conversion_rate: number;
  readonly by_source: readonly {
    readonly source: string;
    readonly open: number;
    readonly won: number;
  }[];
}

/**
 * Las cinco etapas, **copiadas del DTO** y en el orden del embudo.
 *
 * `contactado → demo → prueba → ganado o perdido`. El orden no es alfabético ni el de
 * la base de datos: es el del recorrido, y es lo que hace que el embudo se lea como
 * un embudo.
 */
export const ETAPAS: readonly {
  id: EtapaDeLead;
  etiqueta: string;
  clase: string;
}[] = [
  { id: 'contactado', etiqueta: 'Contactado', clase: 'pastilla-info' },
  { id: 'demo', etiqueta: 'Demo hecha', clase: 'pastilla-info' },
  { id: 'prueba', etiqueta: 'De prueba', clase: 'pastilla-left' },
  { id: 'ganado', etiqueta: 'Ganado', clase: 'pastilla-bien' },
  { id: 'perdido', etiqueta: 'Perdido', clase: 'pastilla-paused' },
];

export const etiquetaDeEtapa = (etapa: string): string =>
  ETAPAS.find((e) => e.id === etapa)?.etiqueta ?? etapa;

export const claseDeEtapa = (etapa: string): string =>
  ETAPAS.find((e) => e.id === etapa)?.clase ?? '';

/** Si la etapa está cerrada. Lo que decide si `days_since_update` significa algo. */
export const etapaCerrada = (etapa: string): boolean =>
  etapa === 'ganado' || etapa === 'perdido';

/**
 * Los seis canales, **copiados del DTO**.
 *
 * Y esta vez se han copiado de verdad: `contacto`, `campeonato`, `seo`,
 * `recomendacion`, `directorio`, `otro`. Van en el orden del DTO y no reordenados,
 * para que se pueda comparar de un vistazo con el contrato.
 */
export const CANALES: readonly { id: CanalDeLead; etiqueta: string }[] = [
  { id: 'contacto', etiqueta: 'Nos escribió' },
  { id: 'campeonato', etiqueta: 'Campeonato' },
  { id: 'seo', etiqueta: 'Nos buscó en Google' },
  { id: 'recomendacion', etiqueta: 'Se lo recomendaron' },
  { id: 'directorio', etiqueta: 'Directorio' },
  { id: 'otro', etiqueta: 'Otro' },
];

/**
 * `landing` no está en `CANALES` porque no se elige a mano: lo pone el formulario
 * de la web al pedir una demo. Pero se nombra en la lista y en «Por canal».
 */
const LANDING = {
  id: 'landing',
  etiqueta: 'Pidió una demo en la web',
} as const;

export const etiquetaDeCanal = (canal: string | null): string =>
  canal === null
    ? 'Sin anotar'
    : canal === LANDING.id
      ? LANDING.etiqueta
      : (CANALES.find((c) => c.id === canal)?.etiqueta ?? canal);

/**
 * `Club Pascual` → `club-pascual`, para proponer la URL al convertir.
 *
 * Es una **propuesta**, no una decisión: el patrón del DTO exige
 * `^[a-z0-9]+(-[a-z0-9]+)*$` y la URL «se decide una vez», así que se deja editar.
 *
 * `normalize('NFD')` y fuera los diacríticos: sin eso «Dojang Pascual Alcalá» sale
 * con la á y el servidor lo rechaza con un patrón, que es la peor forma de explicarlo.
 */
export function comoUrl(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * ¿Está vencida la próxima acción?
 *
 * Se compara en texto `YYYY-MM-DD` y no con `Date`, porque `next_action_on` es una
 * fecha sin hora: convertirla a `Date` la pone a medianoche UTC y al oeste de
 * Greenwich saldría vencida un día antes. Es la misma trampa que `formato.ts`
 * documenta para las fechas de alta.
 */
export function accionVencida(
  next: Date | string | null,
  hoy: string
): boolean {
  if (next === null) return false;
  const dia =
    next instanceof Date
      ? `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`
      : String(next).slice(0, 10);
  return dia < hoy;
}
